import { useState } from 'react';
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import { getAppLink } from '../lib/appLinks';
import { embedPolicyForApp, IFRAME_SANDBOX } from '../lib/embedConfig';
import { EXTERNAL_LINK_PROPS } from '../lib/appLinks';
import { secondaryButtonClass } from './FilterBar';

interface AppEmbedFrameProps {
  appId: string;
  title?: string;
  className?: string;
}

function AppEmbedFrame({ appId, title, className }: AppEmbedFrameProps) {
  const link = getAppLink(appId);
  const policy = embedPolicyForApp(appId);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadError, setLoadError] = useState(false);

  if (!link) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center rounded-xl border border-zt-border bg-zt-surface p-6 text-sm text-zt-muted">
        Unknown app: {appId}
      </div>
    );
  }

  if (policy === 'blocked') {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-4 rounded-xl border border-zt-border bg-zt-surface p-8 text-center">
        <AlertTriangle size={32} className="text-zt-warning" />
        <div>
          <p className="font-medium text-white">{title ?? link.name} cannot be embedded</p>
          <p className="mt-1 text-sm text-zt-muted">
            This app blocks cross-origin iframes. Use Open in app to launch it in a new tab.
          </p>
        </div>
        <a href={link.url} {...EXTERNAL_LINK_PROPS} className={secondaryButtonClass}>
          <ExternalLink size={16} /> Open {link.name}
        </a>
      </div>
    );
  }

  return (
    <div className={['flex h-full min-h-[480px] flex-col', className].filter(Boolean).join(' ')}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs text-zt-muted">
          Embedded {title ?? link.name} · session may require separate login
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setLoadError(false);
              setReloadKey((k) => k + 1);
            }}
            className={secondaryButtonClass}
          >
            <RefreshCw size={14} /> Reload
          </button>
          <a href={link.url} {...EXTERNAL_LINK_PROPS} className={secondaryButtonClass}>
            <ExternalLink size={14} /> Open
          </a>
        </div>
      </div>
      {loadError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-zt-border bg-zt-surface p-6 text-center">
          <AlertTriangle size={28} className="text-zt-warning" />
          <p className="text-sm text-zt-muted">Failed to load embedded app.</p>
          <a href={link.url} {...EXTERNAL_LINK_PROPS} className={secondaryButtonClass}>
            Open in new tab
          </a>
        </div>
      ) : (
        <iframe
          key={reloadKey}
          title={title ?? link.name}
          src={link.url}
          sandbox={IFRAME_SANDBOX}
          className="min-h-0 flex-1 rounded-xl border border-zt-border bg-white"
          referrerPolicy="strict-origin-when-cross-origin"
          onError={() => setLoadError(true)}
        />
      )}
    </div>
  );
}

export default AppEmbedFrame;
